// @flow
import { combineReducers } from 'redux';

import * as common from './common';
import * as types from '../types/routes';
import * as studentTypes from '../types/students';
import * as monitorTypes from '../types/monitors';
import type {
  ROUTE_TYPE,
  ROUTES_ACTION_TYPE,
} from '../types/routes';
import type { ERROR_TYPE, ID_TYPE } from '../types/common';


export type RoutesState = {
  byId: { [number]: ROUTE_TYPE },
  byStudentId: { [number]: [number] },
  fetching: Array<number>,
  order: Array<number>,
  showing: Array<number>,
  current: number,
  toDelete: ID_TYPE,
  isFetching: boolean,
  error: ERROR_TYPE,
  byIdWatchingStopDetail: {[number]: Object},
};

const showing = (
  state: Array<number> = [],
  action: ROUTES_ACTION_TYPE,
): Array<number> => {
  switch (action.type) {
    case types.UPDATE_VISIBLE_ROUTES: {
      return action.payload;
    }
    case types.DELETE_ROUTE_SUCCESS: {
      const { id } = action.payload;
      return state.filter(elm => elm !== id);
    }
    default:
      return state;
  }
};

export const getDescriptionKey = (routeId: number, studentId: number) => `${routeId}-${studentId}`;

const byIdWatchingStopDetail = common.byId({
  added: [types.ROUTE_STOPS_DETAIL_SHOWN],
  updated: [types.ROUTE_STOPS_DETAIL_HIDDEN],
});

const byId = common.byId({
  added: [types.ROUTE_FETCH_SUCCEED, types.CREATE_ROUTE_SUCCESS],
  fetched: [
    types.ROUTES_FETCH_SUCCEED,
    studentTypes.STUDENT_ROUTES_FETCH_SUCCEED,
    monitorTypes.MONITOR_ROUTES_FETCH_SUCCEED,
    studentTypes.STUDENT_ROUTES_UPDATED,
    types.CREATE_ROUTE_SUCCESS,
  ],
  removed: [types.ROUTE_REMOVED, types.DELETE_ROUTE_SUCCESS],
  updated: [
    types.ROUTE_PATH_FETCH_SUCCEED,
    types.ROUTE_UPDATED,
    types.SORT_ROUTE_STOP_SUCCESS,
    types.ROUTES_UPDATE_SUCCEED,
    types.ROUTES_UPDATE_SUCCEED,
  ],
});

const fetching = common.fetching({
  started: [types.ROUTE_FETCH_STARTED, types.SCHOOL_ROUTES_FETCH_STARTED],
  succeed: [
    types.ROUTE_FETCH_SUCCEED,
    types.ROUTE_STUDENT_DESCRIPTION_FETCH_SUCCEED,
    types.SCHOOL_ROUTES_FETCH_STARTED,
  ],
  failed: [types.ROUTE_FETCH_FAILED],
});

const isFetching = common.isFetching({
  started: [
    types.ROUTES_FETCH_STARTED,
    types.SCHOOL_ROUTES_FETCH_STARTED,
    types.DELETE_ROUTE_STARTED,
  ],
  failed: [types.ROUTES_FETCH_FAILED],
  succeed: [types.ROUTES_FETCH_SUCCEED, types.DELETE_ROUTE_SUCCESS],
});

const error = common.error({
  populate: [types.ROUTES_FETCH_FAILED],
  clear: [types.ROUTES_FETCH_STARTED, types.ROUTES_FETCH_SUCCEED],
});

const current = common.mux({
  selected: [types.ROUTE_SELECTED],
  allDeselected: [types.ROUTE_REMOVED, types.ROUTE_DESELECTED],
  default: -1,
});

const toDelete = common.mux({
  selected: [types.ROUTE_TO_DELETE],
  default: -1,
});

const order = common.order({
  fetched: [
    types.ROUTES_FETCH_SUCCEED,
    monitorTypes.MONITOR_ROUTES_FETCH_SUCCEED,
  ],
  removed: [
    types.DELETE_ROUTE_SUCCESS,
  ],
  added: [
    types.ROUTE_FETCH_SUCCEED,
    types.CREATE_ROUTE_SUCCESS,
  ],
});


const routes = combineReducers({
  byId,
  order,
  fetching,
  showing,
  isFetching,
  error,
  current,
  toDelete,
  byIdWatchingStopDetail,
});

export default routes;

export const getCurrentRoute = (state: RoutesState) => (
  state.current > -1
    ? state.byId[state.current]
    : undefined
);
export const getRouteToDelete = (state: RoutesState) => (
  state.toDelete > -1
    ? state.byId[state.toDelete]
    : undefined
);

export const isFetchingRoutes = (state: RoutesState): boolean => state.isFetching;
export const getRoute = (state: RoutesState, id: number): ROUTE_TYPE => state.byId[id];
export const getRoutes = (
  state: RoutesState,
): Array<ROUTE_TYPE> => {
  const routes = state.order.map(id => state.byId[id]);
  routes.sort((a, b) => {
    if (a.short_name < b.short_name) {
      return -1;
    }
    if (a.short_name > b.short_name) {
      return 1;
    }
    return 0;
  });
  return routes;
};
export const getRoutesById = (state: RoutesState): Object => state.byId;
export const getRoutesError = (state: RoutesState): ERROR_TYPE => state.error;
export const getRoutesOrder = (state: RoutesState): Array<number> => state.order;
export const getVisibleRoutes = (
  state: RoutesState,
): Array<ROUTE_TYPE> => {
  const routes = state.showing.map(id => state.byId[id]);
  routes.sort((a, b) => {
    if (a.short_name < b.short_name) {
      return -1;
    }
    if (a.short_name > b.short_name) {
      return 1;
    }
    return 0;
  });
  return routes;
}
export const isRouteFetching = (
  state: RoutesState,
  id: number,
): boolean => state.fetching.includes(id);
export const getRoutesFetchingError = (state: RoutesState): ERROR_TYPE => state.error;
export const getSelectedRoute = (state: RoutesState): number => state.current;
export const getRouteStopsIds = (
  state: RoutesState,
  id: number,
): Array<number> => (state.byId[id] && state.byId[id].stops ? state.byId[id].stops : []);


export const isRouteWatchingStops = (state: RoutesState, id: number): boolean => {
  const { isWatchingStopsDetail = false } = state.byIdWatchingStopDetail[id] || {};
  return isWatchingStopsDetail;
};



// WEBPACK FOOTER //
// ./src/scripts/reducers/routes.js